import {useQuery} from "@tanstack/react-query";
import {useParams} from "react-router-dom";
import {API} from "api";
import {useAuth} from "hooks/useAuth";
import {shouldUseBgDerivation} from "hooks/useBgDerivation";
import {can} from "data/permissions";

/**
 * The editable background list (GET /api/v3/bg-list) — the sidebar gallery with
 * the admin's own draft order, deletions and uploads already applied.
 *
 * Editing is a SUPERADMIN-ONLY capability (`editBgList` in data/permissions). Every other role reads the
 * published list through `/api/v3/backgrounds`, which applies the same
 * `order`/`hidden` and returns the same positions, and sees no editing
 * controls. The server enforces this on each write endpoint as well — the gate
 * here only decides what the UI offers.
 *
 * The rest of the gate is the same narrowness as plan D1. The feature only
 * makes sense where a NUMBERED gallery exists:
 *   * the `bgDerivation` route condition — the legacy tiles path derives order
 *     from S3 folder names and cannot represent a reorder or a deletion, and a
 *     saved scene (`/scene/:id`) must keep rendering exactly what it stored;
 *   * the `bgListEditing` flag;
 *   * a channel that is not `livestream` or `ads` — those two run their own
 *     custom-background flows in BgSelector and have no numbered gallery.
 *
 * Off the gate, everything falls back to `/api/v3/backgrounds` unchanged.
 */

/** Channels whose BgSelector has no numbered gallery to edit. */
const EXCLUDED_CHANNELS = new Set(["livestream", "ads"]);

export interface BgListEntry {
  position: number;
  entryKey: string;
  kind: "catalog" | "upload";
  sourceVariation: number | null;
  /** Set to the POSITION by the server, so positional consumers keep working. */
  variation: number;
  imageUrl: string;
  thumbnailUrl: string;
  miniUrl: string | null;
  outdatedUrl: string | null;
  width: number | null;
  height: number | null;
  base: string | null;
  markers: string[];
  transforms: Record<string, unknown>;
  hasStoredTransforms: boolean;
  productImages?: Record<string, unknown>;
  productTransforms?: Record<string, unknown>;
}

/** Pure form of the gate, mirroring `shouldUseBgDerivation`. */
export function shouldUseBgListEditing({
  featureFlags,
  sceneId,
  channel,
  role,
}: {
  featureFlags: Record<string, unknown> | undefined;
  sceneId: string | undefined;
  channel: string | undefined;
  role: string | null | undefined;
}): boolean {
  if (!can(role, "editBgList")) return false;
  if (featureFlags?.bgListEditing !== true) return false;
  if (!shouldUseBgDerivation({featureEnabled: featureFlags?.bgDerivation === true, sceneId})) {
    return false;
  }
  return Boolean(channel) && !EXCLUDED_CHANNELS.has(channel as string);
}

/** The gate as a hook, for components below App. */
export function useBgListEditing(channel: string | undefined): boolean {
  const {sceneId} = useParams();
  const {featureFlags, role} = useAuth();
  return shouldUseBgListEditing({featureFlags, sceneId, channel, role});
}

/** Query key of the resolved list. Product-specific: the hydrated urls are. */
export const getBgListQueryKey = (
  channel: string | undefined,
  type: string | undefined,
  product: string | undefined
) => ["bg-list", channel, type ?? "general", product ?? null] as const;

/**
 * The PUBLISHED list, ignoring the caller's own arrangement. The comparison
 * target for "your list differs from what everyone else sees".
 */
export function useGlobalBgList({
  channel,
  type,
  product,
  enabled,
}: {
  channel: string | undefined;
  type: string | undefined;
  product: string | undefined;
  enabled: boolean;
}) {
  return useQuery({
    queryKey: [...getBgListQueryKey(channel, type, product), "global"],
    queryFn: () => API.fetchBgList(channel, type, product, "global"),
    // Product is part of every arrangement's identity; the server 400s
    // without it, so don't issue the request until the route resolves one.
    enabled: Boolean(enabled && channel && type && product),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Fetch the resolved list. Inert (idle, `data` undefined) unless `enabled`, so
 * a flag-off route never issues the request.
 */
export function useBgList({
  channel,
  type,
  product,
  enabled,
}: {
  channel: string | undefined;
  type: string | undefined;
  product: string | undefined;
  enabled: boolean;
}) {
  return useQuery({
    queryKey: getBgListQueryKey(channel, type, product),
    queryFn: () => API.fetchBgList(channel, type, product),
    // Product is part of every arrangement's identity; the server 400s
    // without it, so don't issue the request until the route resolves one.
    enabled: Boolean(enabled && channel && type && product),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
